import type {
  SingleAssociatedRegistryResponseType,
  ApiRegistryResponseType,
  RegistryType,
  SingleRegistryResponseType,
} from "@/schemas/digital-files/registry";
import type { EntityAssociateType, EntityType } from "@/schemas/digital-files";
import axios from "@/utils/axios";
import {
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type {
  ApiResolutionResponseType,
  ResolutionFormData,
} from "@/schemas/digital-files/digital-file";

export type FiltersType = {
  filter?: { [key: string]: string };
  order_by?: string;
  page?: number;
  per_page?: number;
  search?: string;
};

// Type for registry item in dropdown with entity reference
export interface RegistryDropdownItem {
  id: string;
  name: string;
  entityId: string;
  entityName: string;
  type: "registry";
}

// Type for entity header in dropdown (non-selectable)
export interface EntityHeaderItem {
  id: string;
  name: string;
  type: "entity-header";
}

// Union type for dropdown items
export type ExpedienteDropdownItem = RegistryDropdownItem | EntityHeaderItem;

// Response type for entities with their registries
export interface EntityWithRegistries {
  entity: EntityType;
  registries: RegistryType[];
}

export const useGetRegistries = (
  entityId?: string,
  filters?: FiltersType,
  enabled?: boolean,
) => {
  return useQuery<ApiRegistryResponseType>({
    queryKey: ["entity-registries", entityId, filters],
    enabled: !!entityId && enabled,
    queryFn: async () => {
      const response = await axios.get<ApiRegistryResponseType>(
        `/admin/v1/expedient-base-registries`,
        {
          params: {
            ...(entityId
              ? { "filter[expedient_base_entity_id]": entityId }
              : {}),
            include_associates: 1,
            ...filters,
            relationships: "associates.applicant,associates.role",
          },
        },
      );
      return response.data;
    },
  });
};

const getRegistry = async (
  id: string,
): Promise<SingleRegistryResponseType | undefined> => {
  const response = await axios.get<SingleRegistryResponseType>(
    `/admin/v1/expedient-base-registries/${id}`,
    {
      params: {
        relationships:
          "baseEntity,dataCenterRelations,formalityRequests,fieldValues,fieldValues.entityField,fieldValues.files,relatedRegistries,formalityRequests.process,formalityRequests.applicant,relatedRegistries.baseEntity,associates.applicant,associates.role,formalityRequests.currentNode",
      },
    },
  );
  return response.data;
};

export const createRegistryDetailsBreadcrumb = (
  queryClient: QueryClient,
  id: string,
) => {
  return async () => {
    try {
      const queryKey = ["registry-detail", id];
      if (!id) {
        return null;
      }
      const response = await queryClient.ensureQueryData({
        queryKey,
        queryFn: async () => {
          const resp = await getRegistry(id);
          return {
            data: {
              name: `${resp?.data?.base_entity?.name}-separator-breadcrumb-${resp?.data?.name}`,
            },
          };
        },
        staleTime: 5 * 60 * 1000,
      });

      return response || null;
    } catch (error) {
      console.error("Error fetching registry details:", error);
      return null;
    }
  };
};

export const useGetRegistry = (id: string = "", enabled: boolean = true) => {
  const shouldFetch = !!id && enabled;
  return useQuery<RegistryType | undefined>({
    queryKey: ["registry-detail", id],
    queryFn: async () => {
      const response = await getRegistry(id);
      return response?.data;
    },
    enabled: shouldFetch,
  });
};

export const useSaveNewRegistry = (entityId: string) => {
  return useMutation({
    mutationFn: async ({
      name,
      uniqueCode,
      registries,
      associates,
      selectedOptions,
      relatedRegistries,
    }: {
      name: string;
      uniqueCode: string;
      registries: object;
      associates: Partial<EntityAssociateType>[];
      selectedOptions: object;
      relatedRegistries?: Array<string>;
    }) => {
      const response = await axios.post<SingleRegistryResponseType>(
        `/admin/v1/expedient-base-registries`,
        {
          expedient_base_entity_id: entityId,
          name: name,
          unique_code: uniqueCode,
          field_values: registries,
          selected_options: selectedOptions,
          associates: associates,
          related_registries: relatedRegistries,
        },
      );
      return response.data;
    },
  });
};

export const useUpdateRegistry = (entityId: string) => {
  return useMutation({
    mutationFn: async ({
      name,
      uniqueCode,
      registryId,
      registries,
      associates,
      selectedOptions,
      relatedRegistries,
    }: {
      registryId: string;
      name: string;
      uniqueCode: string | undefined;
      registries: object;
      associates: Partial<EntityAssociateType>[];
      selectedOptions: object;
      relatedRegistries?: Array<string>;
    }) => {
      if (!registryId)
        throw new Error("Entity ID is required for updating registry");

      const response = await axios.patch<SingleRegistryResponseType>(
        `/admin/v1/expedient-base-registries/${registryId}`,
        {
          expedient_base_entity_id: entityId,
          name: name,
          unique_code: uniqueCode,
          field_values: registries,
          selected_options: selectedOptions,
          associates: associates,
          related_registries: relatedRegistries,
        },
      );
      return response.data;
    },
  });
};

export const useDeleteRegistry = (entityId?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (registryId?: string) => {
      if (!registryId) throw new Error("Registry ID is required for deletion");
      const response = await axios.delete(
        `/admin/v1/expedient-base-registries/${registryId}`,
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["entity-registries", entityId],
      });
    },
  });
};

/**
 * Hook to get all registries for the expediente workflow node.
 * Fetches all registries with their entity information.
 */
export const useGetAllRegistriesForWorkflow = (filters?: FiltersType) => {
  return useQuery<ApiRegistryResponseType>({
    queryKey: ["all-registries-workflow", filters],
    queryFn: async () => {
      const { filter, ...rest } = filters || {};
      const { search } = filter || {};
      const response = await axios.get<ApiRegistryResponseType>(
        `/admin/v1/expedient-base-registries`,
        {
          params: {
            ...rest,
            search: search || undefined,
            include_base_entity: 1,
          },
        },
      );
      return response.data;
    },
  });
};

/**
 * Get auto-generated ID for a new registry.
 * @returns
 */
const getAutoID = async (): Promise<{ data: { code: string } }> => {
  const response = await axios.post(
    `/admin/v1/expedient-base-registries/generate-code`,
  );
  return response.data;
};

export const useGetAutoID = (enabled: boolean) => {
  return useQuery<{ data: { code: string } }>({
    enabled: enabled,
    queryKey: ["generate-auto-id"],
    queryFn: async () => {
      const response = await getAutoID();
      return response;
    },
  });
};

export const useCheckAutoID = () => {
  return useMutation({
    mutationFn: async (newCode: string) => {
      const response = await axios.post<{
        data: {
          is_valid: boolean;
          is_available: boolean;
        };
      }>(`/admin/v1/expedient-base-registries/validate-code`, {
        unique_code: newCode,
      });
      return response.data;
    },
  });
};

export const useGetResolutions = (
  registryId?: string,
  filters?: FiltersType,
  enabled?: boolean,
) => {
  return useQuery<ApiResolutionResponseType>({
    queryKey: ["registry-resolutions", filters],
    enabled: !!registryId && enabled,
    queryFn: async () => {
      const { filter, ...rest } = filters || {};
      const { search, ...others } = filter || {};
      const response = await axios.get(`/admin/v1/resolutions`, {
        params: {
          ...rest,
          filter: { ...others, name: search },
          ...(registryId
            ? { "filter[expedient_base_registry_id]": registryId }
            : {}),
          relationships: "formalityRequest,applicant,files",
        },
      });
      return response.data;
    },
  });
};

export const useSaveNewResolution = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Partial<ResolutionFormData>) => {
      const response = await axios.post(`/admin/v1/resolutions`, {
        ...data,
        status: "APPROVED", //send already approved for manual resolutions
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["registry-resolutions"],
        exact: false,
      });
    },
  });
};

export const useUpdateResolution = (entityId?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Partial<ResolutionFormData>) => {
      const response = await axios.patch(
        `/admin/v1/resolutions/${entityId}`,
        data,
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["expedient-resolutions"],
      });
    },
  });
};

export const useDeleteResolution = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (resolutionId: string) => {
      const response = await axios.delete(
        `/admin/v1/resolutions/${resolutionId}`,
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["registry-resolutions"],
        exact: false,
      });
    },
  });
};

export const useGetRegistryByUniqueCode = () => {
  return useMutation({
    mutationFn: async (
      uniqueCode: string,
    ): Promise<SingleAssociatedRegistryResponseType> => {
      const response = await axios.get(
        `/admin/v1/expedient-base-registries/by-code/${uniqueCode}`,
      );
      return response.data;
    },
  });
};
